import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  Input,
  PageHeader,
  Skeleton,
  Spinner,
  Switch,
} from '@repo/ui'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import {
  AlertCircle,
  Copy,
  Pencil,
  Plus,
  Search,
  Sparkles,
  Star,
  Trash2,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'

import {
  agentsKeys,
  useAgents,
  useCreateAgent,
  useDeleteAgent,
  useDuplicateAgent,
} from '@/hooks/use-agents'
import { useAuth } from '@/hooks/use-auth'
import { api } from '@/lib/api'
import type { AgentListItem } from '@/types/agents'
import { PRO_LIMITS } from '@/types/billing'

export const Route = createFileRoute('/_app/agentes/')({
  component: Agentes2Page,
})

function AgentsSkeleton() {
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 6 }).map((_, index) => (
        <Skeleton key={index} className="h-52 rounded-lg" />
      ))}
    </div>
  )
}

function Agentes2Page() {
  const { currentOrgId } = useAuth()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [showInactive, setShowInactive] = useState(false)
  const [deleting, setDeleting] = useState<AgentListItem | null>(null)

  const { data: response, isLoading, isError } = useAgents(currentOrgId)
  const createAgent = useCreateAgent(currentOrgId)
  const deleteAgent = useDeleteAgent(currentOrgId)
  const duplicateAgent = useDuplicateAgent(currentOrgId)
  const agentCount = response?.total ?? 0
  const agentLimit = PRO_LIMITS.aiAgents ?? Infinity
  const atAgentLimit = agentCount >= agentLimit

  const filtered = useMemo(() => {
    const agents = response?.data ?? []
    const query = search.trim().toLowerCase()
    return agents
      .filter((agent) => (showInactive ? true : agent.isActive))
      .filter((agent) => {
        if (!query) return true
        return (
          agent.name.toLowerCase().includes(query) ||
          (agent.assistantName ?? '').toLowerCase().includes(query) ||
          (agent.description ?? '').toLowerCase().includes(query)
        )
      })
  }, [response?.data, search, showInactive])

  async function createNew() {
    if (!currentOrgId || createAgent.isPending) return
    if (atAgentLimit) {
      toast.error(`Limite de ${agentLimit} agentes IA atingido no plano PRO`)
      return
    }
    const agent = await createAgent.mutateAsync({
      name: 'Novo agente',
      instructions:
        'Você é um assistente de atendimento ao cliente via WhatsApp. Responda de forma breve, cordial e direta em português do Brasil.',
      model: 'gpt-4o-mini',
      isActive: true,
    })
    toast.success('Agente criado')
    void navigate({ to: '/agentes/$agentId', params: { agentId: agent.id } })
  }

  async function handleDuplicate(agent: AgentListItem) {
    if (atAgentLimit) {
      toast.error(`Limite de ${agentLimit} agentes IA atingido no plano PRO`)
      return
    }
    const newAgent = await duplicateAgent.mutateAsync(agent.id)
    toast.success(`"${agent.name}" duplicado`)
    void navigate({
      to: '/agentes/$agentId',
      params: { agentId: newAgent.id },
    })
  }

  async function removeAgent() {
    if (!deleting) return
    await deleteAgent.mutateAsync(deleting.id)
    toast.success('Agente removido')
    setDeleting(null)
  }

  async function setAsDefault(agent: AgentListItem) {
    if (!currentOrgId) return
    await api.put(`/orgs/${currentOrgId}/agents/${agent.id}`, {
      isDefault: true,
    })
    toast.success(`"${agent.name}" definido como padrão`)
    void qc.invalidateQueries({ queryKey: agentsKeys.list(currentOrgId) })
  }

  if (!currentOrgId) {
    return (
      <div className="text-muted-foreground flex flex-1 items-center justify-center">
        Selecione uma organização
      </div>
    )
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <PageHeader
        icon={Sparkles}
        title="Agentes"
        description="Configuração de agentes que respondem conversas automaticamente"
        meta={
          <>
            <Badge variant="secondary">
              {agentCount} / {agentLimit}
            </Badge>
          </>
        }
        actions={
          <>
            <Button
              type="button"
              onClick={() => void createNew()}
              disabled={createAgent.isPending || atAgentLimit}
              title={
                atAgentLimit
                  ? `Limite de ${agentLimit} agentes atingido`
                  : undefined
              }
            >
              {createAgent.isPending ? (
                <Spinner />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              Novo agente
            </Button>
          </>
        }
      />

      <div className="flex flex-wrap items-center justify-between gap-3 border-b px-6 py-3">
        <div className="relative w-full max-w-sm">
          <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-3.5 w-3.5 -translate-y-1/2" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar agente..."
            className="pl-9"
          />
        </div>
        <label className="text-muted-foreground flex cursor-pointer items-center gap-2 text-sm">
          <Switch checked={showInactive} onCheckedChange={setShowInactive} />
          Mostrar inativos
        </label>
      </div>

      <main className="min-h-0 flex-1 overflow-auto p-6">
        {isError ? (
          <div className="border-destructive/30 bg-destructive/10 text-destructive flex items-center gap-2 rounded-lg border px-4 py-3 text-sm">
            <AlertCircle className="h-4 w-4" />
            Erro ao carregar agentes.
          </div>
        ) : isLoading ? (
          <AgentsSkeleton />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={Sparkles}
            title="Nenhum agente"
            description="Crie um agente IA para responder automaticamente quando a IA estiver ativa na conversa."
            action={
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void createNew()}
                disabled={createAgent.isPending || atAgentLimit}
              >
                <Plus className="h-3.5 w-3.5" />
                Criar primeiro agente
              </Button>
            }
          />
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((agent) => (
              <div
                key={agent.id}
                className={`group rounded-xl border p-5 transition-colors ${
                  agent.isDefault
                    ? 'border-primary/40 bg-primary/5'
                    : 'border-border bg-card'
                } ${!agent.isActive ? 'opacity-60' : ''}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <Link
                    to="/agentes/$agentId"
                    params={{ agentId: agent.id }}
                    className="bg-primary/10 hover:bg-primary/20 flex h-9 w-9 items-center justify-center rounded-lg transition-colors"
                  >
                    <Sparkles className="text-primary h-4 w-4" />
                  </Link>
                  <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                    {!agent.isDefault && agent.isActive ? (
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        onClick={() => void setAsDefault(agent)}
                        title="Definir como padrão"
                      >
                        <Star className="h-3.5 w-3.5" />
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      asChild
                      title="Editar"
                    >
                      <Link
                        to="/agentes/$agentId"
                        params={{ agentId: agent.id }}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Link>
                    </Button>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      onClick={() => void handleDuplicate(agent)}
                      disabled={duplicateAgent.isPending}
                      title="Duplicar agente"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      onClick={() => setDeleting(agent)}
                      title="Remover"
                    >
                      <Trash2 className="text-destructive h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>

                <Link
                  to="/agentes/$agentId"
                  params={{ agentId: agent.id }}
                  className="mt-3 block hover:underline"
                >
                  <h3 className="truncate text-sm font-semibold tracking-tight">
                    {agent.name}
                  </h3>
                  {agent.assistantName ? (
                    <p className="text-muted-foreground mt-0.5 text-xs">
                      Assistente:{' '}
                      <span className="text-foreground">
                        {agent.assistantName}
                      </span>
                    </p>
                  ) : null}
                  {agent.description ? (
                    <p className="text-muted-foreground mt-1 line-clamp-2 text-xs">
                      {agent.description}
                    </p>
                  ) : null}
                </Link>

                <div className="mt-4 flex flex-wrap items-center gap-1.5">
                  <Badge
                    variant="outline"
                    className={
                      agent.isActive
                        ? 'border-success/30 bg-success/10 text-success'
                        : 'border-border text-muted-foreground'
                    }
                  >
                    {agent.isActive ? 'Habilitado' : 'Desabilitado'}
                  </Badge>
                  {agent.isDefault ? (
                    <Badge className="bg-primary/15 text-primary hover:bg-primary/15">
                      <Star className="mr-1 h-3 w-3" />
                      Padrão da org
                    </Badge>
                  ) : null}
                  {agent._count.channels > 0 ? (
                    <Badge variant="outline" className="text-2xs">
                      {agent._count.channels} canal
                      {agent._count.channels !== 1 ? 'is' : ''}
                    </Badge>
                  ) : null}
                  {agent._count.departmentDefaults > 0 ? (
                    <Badge variant="outline" className="text-2xs">
                      {agent._count.departmentDefaults} depto
                      {agent._count.departmentDefaults !== 1 ? 's' : ''}
                    </Badge>
                  ) : null}
                  {!agent.isDefault &&
                  agent._count.channels === 0 &&
                  agent._count.departmentDefaults === 0 ? (
                    <Badge
                      variant="outline"
                      className="border-warning/30 bg-warning/10 text-warning text-2xs"
                    >
                      Sem atribuição
                    </Badge>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(open) => !open && setDeleting(null)}
        title="Remover agente?"
        description={
          deleting ? `"${deleting.name}" será removido da organização.` : ''
        }
        confirmLabel="Remover"
        destructive
        onConfirm={() => void removeAgent()}
      />
    </div>
  )
}
